/* Motion is progressive enhancement: content stays visible without JavaScript. */
(() => {
  'use strict';
  if (window.NeutralsMotion) return;
  const configNode = document.getElementById('nb-motion-config');
  const config = configNode ? JSON.parse(configNode.textContent) : {};
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const editor = config.editor || window.Shopify?.designMode;
  const duration = Math.max(200, Math.min(1000, Number(config.duration) || 600));
  const ease = 'cubic-bezier(.22,1,.36,1)';
  const accordionStates = new Map(), drawerStates = new Map(), reveals = new Map();
  let observer;
  const enabled = kind => config[kind] !== false && !reduced.matches;

  function settleAccordion(details, state) {
    if (accordionStates.get(details) !== state) return;
    state.animation?.cancel();
    details.open = state.expanded;
    details.style.height = '';
    details.style.overflow = '';
    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', String(state.expanded));
    details.dataset.nbExpanded = String(state.expanded);
    accordionStates.delete(details);
  }

  function toggleAccordion(details, expanded) {
    const summary = details.querySelector(':scope > summary');
    if (!summary) return;
    const start = details.getBoundingClientRect().height;
    accordionStates.get(details)?.animation?.cancel();
    const state = { expanded, animation: null };
    accordionStates.set(details, state);
    details.dataset.nbExpanded = String(expanded);
    summary.setAttribute('aria-expanded', String(expanded));
    if (!enabled('accordions') || !details.animate) return settleAccordion(details, state);
    details.style.height = '';
    details.style.overflow = 'hidden';
    // Measure both natural states; keep the content mounted until collapse finishes.
    details.open = false;
    const closedHeight = details.getBoundingClientRect().height;
    details.open = true;
    const end = expanded ? details.getBoundingClientRect().height : closedHeight;
    details.style.height = start + 'px';
    state.animation = details.animate([{ height: start + 'px' }, { height: end + 'px' }], {
      duration: Math.min(420, duration), easing: 'cubic-bezier(.4,0,.2,1)', fill: 'forwards'
    });
    state.animation.onfinish = () => settleAccordion(details, state);
  }

  function show(element, animate = true) {
    observer?.unobserve(element);
    if (!element.classList.contains('nb-reveal-pending')) return;
    element.classList.remove('nb-reveal-pending');
    if (!animate || !enabled('reveal') || editor || !element.animate) return;
    const animation = element.animate([
      { opacity: 0, transform: 'translateY(18px)' },
      { opacity: 1, transform: 'none' }
    ], { duration, easing: ease });
    reveals.set(element, animation);
    animation.onfinish = () => reveals.delete(element);
  }

  function prepareReveal(element) {
    if (element.dataset.nbReveal || element.closest('dialog,[hidden]')) return;
    element.dataset.nbReveal = 'true';
    // First-screen content and scroll-restored content are immediately readable.
    if (element.getBoundingClientRect().top < innerHeight - 36) return;
    element.classList.add('nb-reveal-pending');
    observer.observe(element);
  }

  function init(scope = document) {
    scope.querySelectorAll('.nb details').forEach(details => {
      if (details.closest('.shopify-app-block')) return;
      const summary = details.querySelector(':scope > summary');
      summary?.setAttribute('aria-expanded', String(details.open));
      if (!accordionStates.has(details)) details.dataset.nbExpanded = String(details.open);
    });
    if (!enabled('reveal') || editor || !('IntersectionObserver' in window)) return;
    if (!observer) observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) show(entry.target);
    }), { threshold: 0, rootMargin: '0px 0px -36px 0px' });
    scope.querySelectorAll('#MainContent .nb-section .nb-container, .nb-section .nb-product .nb-container').forEach(container => {
      if (container.closest('.nb-product')) {
        if (container.closest('.nb-sticky-buy')) return;
        container.querySelectorAll('.nb-pdp-details > *, .nb-gallery-main').forEach(prepareReveal);
      } else if (container.querySelector('[data-nb-selection-bar]')) {
        container.querySelectorAll('[data-nb-shop] > :not([data-nb-selection-bar])').forEach(prepareReveal);
      } else prepareReveal(container);
    });
    scope.querySelectorAll('.nb-footer-grid > div').forEach(prepareReveal);
  }

  function drawer(dialog, opening, done = () => {}) {
    const previous = drawerStates.get(dialog);
    const currentTransform = previous ? getComputedStyle(dialog).transform : null;
    const currentOpacity = previous ? getComputedStyle(dialog).opacity : null;
    previous?.animation?.cancel();
    const state = { opening, done, animation: null };
    drawerStates.set(dialog, state);
    dialog.classList.toggle('nb-drawer-closing', !opening);
    const finish = () => {
      if (drawerStates.get(dialog) !== state) return;
      state.animation?.cancel();
      dialog.classList.remove('nb-drawer-closing');
      drawerStates.delete(dialog);
      done();
    };
    state.finish = finish;
    if (!enabled('drawers') || !dialog.animate) return finish();
    state.animation = dialog.animate(opening
      ? [{ transform: currentTransform || 'translateX(100%)', opacity: currentOpacity || .7 }, { transform: 'translateX(0)', opacity: 1 }]
      : [{ transform: currentTransform || getComputedStyle(dialog).transform, opacity: currentOpacity || 1 }, { transform: 'translateX(100%)', opacity: .7 }],
    { duration: opening ? 360 : 250, easing: ease, fill: 'forwards' });
    state.animation.onfinish = finish;
  }

  function gallery(image) {
    if (enabled('drawers') && image?.animate) image.animate([{ opacity: .3 }, { opacity: 1 }], { duration: 260, easing: 'ease-out' });
  }

  document.addEventListener('click', event => {
    const summary = event.target.closest('.nb details > summary');
    if (!summary || summary.closest('.shopify-app-block') || event.target.closest('a,input,button')) return;
    event.preventDefault();
    const details = summary.parentElement;
    const expanded = !(accordionStates.get(details)?.expanded ?? details.open);
    if (expanded) {
      const group = details.closest('[data-nb-accordion-group]');
      group?.querySelectorAll(':scope > details').forEach(other => {
        if (other !== details && (accordionStates.get(other)?.expanded ?? other.open)) toggleAccordion(other, false);
      });
    }
    toggleAccordion(details, expanded);
  });
  document.addEventListener('focusin', event => {
    let element = event.target.closest('.nb-reveal-pending');
    while (element) { show(element, false); element = element.parentElement?.closest('.nb-reveal-pending'); }
  });
  function showAll() {
    document.querySelectorAll('.nb-reveal-pending').forEach(element => show(element, false));
    reveals.forEach(animation => animation.cancel()); reveals.clear();
  }
  reduced.addEventListener('change', () => {
    if (reduced.matches) {
      showAll();
      accordionStates.forEach((state, element) => settleAccordion(element, state));
      drawerStates.forEach(state => state.finish());
    } else init();
  });
  window.addEventListener('beforeprint', showAll);
  window.addEventListener('pageshow', event => { if (event.persisted) showAll(); });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:select', showAll);
  document.addEventListener('shopify:block:select', showAll);
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-nb-reveal]').forEach(element => { observer?.unobserve(element); reveals.get(element)?.cancel(); reveals.delete(element); });
    event.target.querySelectorAll('details').forEach(element => { accordionStates.get(element)?.animation?.cancel(); accordionStates.delete(element); });
  });
  document.addEventListener('neutrals:cart-update', () => init());
  window.NeutralsMotion = { init, drawer, gallery, showAll };
  init();
})();
